import { Dropdown, Modal, Spin } from 'antd';
import { CaretDownIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import {
  usePauseWorkflowMutation,
  useResumeWorkflowMutation,
  useStopWorkflowMutation,
  useRerunWorkflowMutation,
} from '../../../store/workflows/workflowApi';
import { removeUnderscoresAndCapitalize } from '@admin-panel/utils';

const StatusCircle = ({ color }: { color: string }) => (
  <span
    style={{
      display: 'inline-block',
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: color,
      marginRight: 8,
      verticalAlign: 'middle',
    }}
  />
);

const StatusActions = ({ status, id }: { status: string; id: number }) => {
  const [pauseWorkflow, { isLoading: isPausing }] = usePauseWorkflowMutation();
  const [resumeWorkflow, { isLoading: isResuming }] = useResumeWorkflowMutation();
  const [stopWorkflow, { isLoading: isStopping }] = useStopWorkflowMutation();
  const [rerunWorkflow, { isLoading: isRerunning }] = useRerunWorkflowMutation();
  const [menuLoading, setMenuLoading] = useState(false);

  const handleAction = async ({ key }: { key: string }) => {
    setMenuLoading(true);
    try {
      if (key === 'pause') await pauseWorkflow({ id }).unwrap();
      if (key === 'resume') await resumeWorkflow({ id }).unwrap();
      if (key === 'stop') await stopWorkflow({ id }).unwrap();
      if (key === 'rerun') {
        Modal.confirm({
          title: 'Rerun Workflow',
          content: `
            Rerunning this workflow will restart all its steps and may incur
            additional costs or duplicate data.
          `,
          okText: 'Rerun',
          okType: 'primary',
          cancelText: 'Cancel',
          onOk: async () => {
            setMenuLoading(true);
            try {
              await rerunWorkflow({ id }).unwrap();
            } finally {
              setMenuLoading(false);
            }
          },
        });
        return;
      }
    } finally {
      setMenuLoading(false);
    }
  };

  let circleColor = 'gray';
  if (status === 'RUNNING') circleColor = 'green';
  else if (status === 'PAUSED') circleColor = 'orange';
  else if (status === 'COMPLETED') circleColor = 'blue';
  else if (status === 'STOPPED') circleColor = 'red';

  let menuItems: Array<{
    key: string;
    label: React.ReactNode;
    disabled?: boolean;
  }> = [];

  switch (status) {
    case 'RUNNING': {
      menuItems = [
        {
          key: 'pause',
          label: (
            <>
              <StatusCircle color="orange" />
              {isPausing ? <Spin size="small" /> : 'Pause'}
            </>
          ),
          disabled: isPausing,
        },
        {
          key: 'stop',
          label: (
            <>
              <StatusCircle color="red" />
              {isStopping ? <Spin size="small" /> : 'Stop'}
            </>
          ),
          disabled: isStopping,
        },
        {
          key: 'rerun',
          label: (
            <>
              <StatusCircle color="blue" />
              {isRerunning ? <Spin size="small" /> : 'Rerun'}
            </>
          ),
          disabled: isRerunning,
        },
      ];
      break;
    }
    case 'PAUSED': {
      menuItems = [
        {
          key: 'resume',
          label: (
            <>
              <StatusCircle color="green" />
              {isResuming ? <Spin size="small" /> : 'Resume'}
            </>
          ),
          disabled: isResuming,
        },
        {
          key: 'rerun',
          label: (
            <>
              <StatusCircle color="blue" />
              {isRerunning ? <Spin size="small" /> : 'Rerun'}
            </>
          ),
          disabled: isRerunning,
        },
      ];
      break;
    }
    case 'COMPLETED': {
      menuItems = [
        {
          key: 'rerun',
          label: (
            <>
              <StatusCircle color="blue" />
              {isRerunning ? <Spin size="small" /> : 'Rerun'}
            </>
          ),
          disabled: isRerunning,
        },
      ];
      break;
    }
    case 'STOPPED': {
      menuItems = [
        {
          key: 'resume',
          label: (
            <>
              <StatusCircle color="green" />
              {isResuming ? <Spin size="small" /> : 'Resume'}
            </>
          ),
          disabled: isResuming,
        },
        {
          key: 'rerun',
          label: (
            <>
              <StatusCircle color="blue" />
              {isRerunning ? <Spin size="small" /> : 'Rerun'}
            </>
          ),
          disabled: isRerunning,
        },
      ];
      break;
    }
    default:
      menuItems = [];
  }

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: handleAction,
      }}
      trigger={['click']}
    >
      <span
        style={{
          cursor: menuLoading ? 'wait' : 'pointer',
          minWidth: 90,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <StatusCircle color={circleColor} />
        {menuLoading ? <Spin size="small" /> : removeUnderscoresAndCapitalize(status)}
        <CaretDownIcon style={{ fontSize: 12, marginLeft: 4 }} />
      </span>
    </Dropdown>
  );
};

export default StatusActions;
